// Keep slow-moving data between visits (owner, 2026-09-24: "hire dates are
// static as are birthdays, names … hours and pay should be static from the day
// it updates after payroll till the next payroll run … right now it loads over
// and over").
//
// Each visit asks the server ONE small question — GET /api/v1/data-versions,
// a fingerprint per group — and compares it with the fingerprint saved last
// visit. A group whose fingerprint did not move keeps its saved copy with no
// refetch at all; one that moved is dropped and loads fresh. A different
// signed-in person or a different "Viewing as" lens drops everything, because
// what was saved is only right for the viewer it was saved for.
//
// Fast-moving data (tasks, money, Manager Entry, ideas) is not in any group and
// loads fresh exactly as before.
//
// Seen live, 2026-09-25: a page that is already on screen when this check
// answers (a reload on /recruiting, a direct landing on /people) has already
// started revalidating its saved copy — the copy paints first, one request
// follows. The keep is total only for what is opened after the check: My
// Desk first, then the page. Recruiting's Archive tab opened after a reload
// made no request at all.

import { authedGet } from './apiClient';
import { LOCATION_ORDER } from './locations';
import {
  confirmMirrored, dropEntityTables, dropResources, keepAcrossVisits, versionCheckSettled,
} from './store';

export interface DataGroup { resources: string[]; tables: string[] }

export type DataGroupName =
  | 'people' | 'payroll' | 'company' | 'recruiting_archive' | 'recruiting_live' | 'tasks' | 'desk' | 'money' | 'reports';

/** Which saved data each server fingerprint covers. A key ending in ':' or
 * '-' is a prefix. A key in two groups is dropped when EITHER moves. */
export const DATA_GROUPS: Record<DataGroupName, DataGroup> = {
  people: {
    // service-compliance is counted from people AND their licenses, so it
    // sits in both the people and company groups (dropped when either moves).
    resources: ['people-roster', 'people-inactive', 'person-profile:', 'people-needs-details:', 'people-mgmt:', 'service-compliance'],
    tables: [],
  },
  payroll: {
    resources: [
      'pay-hours:', 'hour-raises:', 'people-mgmt:', 'person-profile:',
      'payroll-review-documents', 'payroll-review-document-', 'payroll-week-',
    ],
    tables: ['payPeriod', 'payrollReview', 'payrollExtra', 'registerLine'],
  },
  company: {
    resources: [
      'company', 'company-vendors', 'company-vendor:', 'company-licenses', 'company-leases',
      'company-insurance', 'login-links', 'service-compliance',
    ],
    tables: [],
  },
  // Tasks and the Desk panels (owner, 2026-09-26: every reload was asking for
  // the task list, the standing list, Communications, Intake and Ideas again
  // — 14 requests before anything changed). The ticks and standing list live
  // in the entity tables; lib/bootstrap.ts's tasks slice skips its load when
  // 'task-periods' was confirmed AND both tables are held. The server's
  // `tasks` fingerprint carries the date, and refreshTasksIfNewDay still
  // reloads at the clinic's own midnight.
  tasks: {
    resources: ['task-periods', 'task-ticks-day'],
    tables: ['task', 'standing'],
  },
  desk: {
    resources: ['communications', 'intake', 'automation-ideas:', 'favorites', 'desk-layout:'],
    tables: [],
  },
  // Money and Reports (2026-09-26): their tables were kept whenever the
  // mirror held them, with nothing to say when someone else had added a
  // row. Now the server's fingerprint drops them the moment a deposit,
  // count, cash line, report run or work item changes anywhere.
  money: {
    resources: ['deposit-recon-year:'],
    tables: ['deposit', 'drawerCount', 'pettyCash', 'otherCashItem', 'chargeback', 'depositRecon', 'depositBatch'],
  },
  reports: {
    resources: [],
    tables: ['reportDefinition', 'reportShelf', 'reportRun', 'reportRow', 'workItem'],
  },
  // Recruiting (owner, 2026-09-25: "all past records are static and only
  // those active get updates"). The Archive tab's pages and the cards of
  // people with an outcome move only when one of THEM changes; the summary
  // tiles, the clinic / All clinics pages and the cards of people still in
  // play move on any live write and at least once a day. Key shapes:
  // lib/recruitingApi.ts (a card's key names its side of the line).
  recruiting_archive: {
    resources: ['recruiting-candidates:ARCHIVE:', 'recruiting-candidate:past:'],
    tables: [],
  },
  recruiting_live: {
    resources: [
      'recruiting-summary',
      'recruiting-new-applications',
      ...['ALL', ...LOCATION_ORDER].map((code) => `recruiting-candidates:${code}:`),
      'recruiting-candidate:live:',
    ],
    tables: [],
  },
};

keepAcrossVisits(Object.values(DATA_GROUPS).flatMap((g) => g.resources));

const STORE_KEY = 'nv-data-versions';

interface Saved { viewer: string; versions: Record<string, string> }

function readSaved(): Saved | null {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    return raw ? (JSON.parse(raw) as Saved) : null;
  } catch {
    return null;
  }
}

function writeSaved(saved: Saved): void {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(saved)); } catch { /* storage unavailable — next visit just reloads */ }
}

export function forgetDataVersions(): void {
  try { localStorage.removeItem(STORE_KEY); } catch { /* nothing to forget */ }
  lastDecision = null;
}

let lastDecision: Record<string, 'kept' | 'reloaded'> | null = null;

/** True only when THIS visit's check said the group did not move. A failed
 * check, or no check yet, is never "kept" — lib/bootstrap.ts's tasks slice
 * asks this before trusting last visit's ticks. */
export function groupKeptThisVisit(group: DataGroupName): boolean {
  return lastDecision !== null && lastDecision[group] === 'kept';
}

/** Confirm or drop each group. Returns what it decided, for tests and logs. A
 * failed check changes nothing — the saved copies simply revalidate as before. */
export async function syncDataVersions(viewer: string): Promise<Record<string, 'kept' | 'reloaded'> | null> {
  try {
    return await syncOnce(viewer);
  } finally {
    // Kept keys that mounted early may be waiting on this (lib/store.ts).
    versionCheckSettled();
  }
}

async function syncOnce(viewer: string): Promise<Record<string, 'kept' | 'reloaded'> | null> {
  let server: Record<string, string>;
  try {
    server = await authedGet<Record<string, string>>('/api/v1/data-versions');
  } catch {
    return null;
  }
  const saved = readSaved();
  const sameViewer = saved !== null && saved.viewer === viewer;
  const decided: Record<string, 'kept' | 'reloaded'> = {};
  const moved = (Object.keys(DATA_GROUPS) as DataGroupName[])
    .filter((g) => !(sameViewer && server[g] && saved!.versions[g] === server[g]));
  // Drop first (a key in two groups goes if either moved), then confirm the rest.
  for (const g of moved) {
    await dropResources(DATA_GROUPS[g].resources);
    await dropEntityTables(DATA_GROUPS[g].tables);
    decided[g] = 'reloaded';
  }
  for (const g of Object.keys(DATA_GROUPS) as DataGroupName[]) {
    if (moved.includes(g)) continue;
    confirmMirrored(DATA_GROUPS[g].resources);
    decided[g] = 'kept';
  }
  writeSaved({ viewer, versions: server });
  lastDecision = decided;
  return decided;
}
